const VERSION_ENDPOINT = '/version.json';
const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const AUTO_RELOAD_KEY_PREFIX = 'picsect:auto-reloaded:';
const CHUNK_RELOAD_KEY = 'picsect:chunk-reload-at';
const CHUNK_RELOAD_COOLDOWN_MS = 60 * 1000;

const CURRENT_BUILD = Object.freeze({
  version: process.env.REACT_APP_BUILD_VERSION || 'development',
  builtAt: process.env.REACT_APP_BUILD_TIME || null,
});

function parseBuildTime(value) {
  const timestamp = Date.parse(value || '');
  return Number.isFinite(timestamp) ? timestamp : null;
}

function isNewerBuild(latest, current = CURRENT_BUILD) {
  if (!latest?.version || latest.version === current.version) return false;
  if (current.version === 'development') return false;

  const latestTime = parseBuildTime(latest.builtAt);
  const currentTime = parseBuildTime(current.builtAt);
  if (latestTime !== null && currentTime !== null) return latestTime > currentTime;

  // Older deployments may not have a timestamp. A different stable version is
  // still the strongest signal available during the compatibility period.
  return true;
}

async function fetchLatestBuild(fetchImpl = window.fetch) {
  const separator = VERSION_ENDPOINT.includes('?') ? '&' : '?';
  const response = await fetchImpl(`${VERSION_ENDPOINT}${separator}t=${Date.now()}`, {
    cache: 'no-store',
    credentials: 'same-origin',
    headers: { 'Cache-Control': 'no-cache' },
  });
  if (!response.ok) throw new Error(`Version check failed with ${response.status}`);
  return response.json();
}

function isSafeAutoReloadPath(pathname) {
  return ['/', '/login', '/signup', '/forgetPassword'].includes(pathname);
}

function hasAutoReloaded(version, storage = window.sessionStorage) {
  return storage.getItem(`${AUTO_RELOAD_KEY_PREFIX}${version}`) === '1';
}

function markAutoReloaded(version, storage = window.sessionStorage) {
  storage.setItem(`${AUTO_RELOAD_KEY_PREFIX}${version}`, '1');
}

function getErrorMessage(value) {
  if (typeof value === 'string') return value;
  return String(value?.message || value?.reason?.message || value?.reason || '');
}

function isChunkLoadError(value) {
  const message = getErrorMessage(value);
  return /ChunkLoadError|Loading chunk [\w-]+ failed|Failed to fetch dynamically imported module|Importing a module script failed/i.test(message);
}

function installChunkLoadRecovery({
  target = window,
  storage = window.sessionStorage,
  reload = () => window.location.reload(),
  now = () => Date.now(),
} = {}) {
  const recover = (event) => {
    if (!isChunkLoadError(event)) return;
    const currentTime = now();
    const previousTime = Number(storage.getItem(CHUNK_RELOAD_KEY) || 0);
    if (currentTime - previousTime < CHUNK_RELOAD_COOLDOWN_MS) return;
    storage.setItem(CHUNK_RELOAD_KEY, String(currentTime));
    reload();
  };

  target.addEventListener('error', recover);
  target.addEventListener('unhandledrejection', recover);
  return () => {
    target.removeEventListener('error', recover);
    target.removeEventListener('unhandledrejection', recover);
  };
}

export {
  AUTO_RELOAD_KEY_PREFIX,
  CHUNK_RELOAD_COOLDOWN_MS,
  CHUNK_RELOAD_KEY,
  CURRENT_BUILD,
  VERSION_CHECK_INTERVAL_MS,
  VERSION_ENDPOINT,
  fetchLatestBuild,
  hasAutoReloaded,
  installChunkLoadRecovery,
  isChunkLoadError,
  isNewerBuild,
  isSafeAutoReloadPath,
  markAutoReloaded,
};
